import {
  AggregationPeriod,
  AggregationPeriodConfig,
} from "./TrendsOverTimeChart.types";

type DefaultAggregationPeriodsMap = Record<
  AggregationPeriod,
  AggregationPeriodConfig
>;

export const DEFAULT_AGGREGATION_PERIODS: DefaultAggregationPeriodsMap = {
  daily: {
    label: "Giornaliero",
    aggregationKey: (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toISOString().split("T")[0]; // YYYY-MM-DD
    },
    displayLabel: (dateKey: string) => new Date(dateKey).toLocaleDateString(),
    sort: (a: string, b: string) => {
      return new Date(a).getTime() - new Date(b).getTime();
    },
  },
  weekly: {
    label: "Settimanale",
    aggregationKey: (timestamp: number) => {
      const date = new Date(timestamp);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      return weekStart.toISOString().split("T")[0];
    },
    displayLabel: (dateKey: string) => {
      const weekStart = new Date(dateKey);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
    },
    sort: (a: string, b: string) => {
      return new Date(a).getTime() - new Date(b).getTime();
    },
  },
  monthly: {
    label: "Mensile",
    aggregationKey: (timestamp: number) => {
      const date = new Date(timestamp);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`; // YYYY-MM
    },
    displayLabel: (dateKey: string) => {
      const [year, month] = dateKey.split("-").map(Number);
      // Use UTC to avoid timezone issues
      const date = new Date(Date.UTC(year, month - 1, 1));
      return date.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "long",
        timeZone: "UTC",
      });
    },
    sort: (a: string, b: string) => {
      // Parse YYYY-MM strings directly for more reliable comparison
      const [yearA, monthA] = a.split("-").map(Number);
      const [yearB, monthB] = b.split("-").map(Number);
      const yearCompare = yearA - yearB;
      if (yearCompare !== 0) return yearCompare;
      return monthA - monthB;
    },
  },
  quarterly: {
    label: "Trimestrale",
    aggregationKey: (timestamp: number) => {
      const date = new Date(timestamp);
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`; // YYYY-Q1
    },
    displayLabel: (dateKey: string) => dateKey, // Already formatted as YYYY-Q1
    sort: (a: string, b: string) => {
      const [yearA, quarterA] = a.split("-");
      const [yearB, quarterB] = b.split("-");
      const yearCompare = parseInt(yearA) - parseInt(yearB);
      if (yearCompare !== 0) return yearCompare;
      return (
        parseInt(quarterA.replace("Q", "")) -
        parseInt(quarterB.replace("Q", ""))
      );
    },
  },
  yearly: {
    label: "Annuale",
    aggregationKey: (timestamp: number) => {
      const date = new Date(timestamp);
      return String(date.getFullYear()); // YYYY
    },
    displayLabel: (dateKey: string) => dateKey, // Already formatted as YYYY
    sort: (a: string, b: string) => parseInt(a) - parseInt(b),
  },
}; // Default aggregation period keys
